/**
 * Mana Pricing page — API shapes and small helpers shared by its components.
 */
import {
  PRICING_FIELDS,
  dscrFromRent,
  fieldOptionsFor,
  fieldValueList,
  hasDscrLoanType,
  emailLoanPurposeLabel,
  emailLoanTypeLabel,
  loanTypesOf,
  resolvePricingCategory,
  type LenderRateRow,
  type ManaEmailTemplate,
  type ManaGridCell,
  type ManaGridParams,
  type ManaOptionsSentRow,
  type ManaPricingSource,
  type RealtorPicks,
  type PricingCompCategory,
  type PricingFieldValue,
  type UwmCriteriaByFamily,
  type UwmFieldValue,
  type UwmLayoutField,
  type UwmQuoteResult,
} from '@mana/shared';
import { ApiError } from '../../lib/api.js';
import type { QuoteResponse, QuoteRow } from '../OtherLendersResults.js';
import { fmtCT, fmtSheetStamp } from '../../lib/time.js';

export type ProductType = 'mortgage' | 'heloc';
export type CanonicalValues = Record<string, PricingFieldValue>;
export type UwmValues = Record<string, UwmFieldValue>;
export type Msg = { tone: 'ok' | 'err' | 'info'; text: string } | null;

/** A rate-sheet row as the Mana Pricing API returns it (with the sheet's publish time). */
export type SheetQuoteRow = QuoteRow & { sheetAt: string | null };
export type SheetQuote = Omit<QuoteResponse, 'rows'> & { rows: SheetQuoteRow[] };

export type UwmOutcome =
  | {
      status: 'ok';
      result: UwmQuoteResult;
      quotedAt: string;
      criteria: { comp: number; closing: number } | null;
      category: PricingCompCategory | null;
      lpCompPct: number | null;
      /** ★ criteria per loan type family when UWM priced several (products carry manaLoanFamily). */
      criteriaByFamily?: UwmCriteriaByFamily;
      /** Loan types UWM could not price this time. */
      notes?: string[];
    }
  | { status: 'error'; code: string; message: string; fieldErrors: Record<string, string[]> | null; retryAfterSeconds: number | null }
  | { status: 'skipped'; reason: string };
export type SheetsOutcome = { status: 'ok'; quote: SheetQuote } | { status: 'error'; message: string } | { status: 'skipped'; reason: string };

export interface QuoteApiResponse {
  savedAs: string;
  lastQuotedAt: string | null;
  uwm: UwmOutcome;
  sheets: SheetsOutcome;
  /** Picked sources still being priced (the slow engines answer in a second step). */
  pending?: string[];
}

/**
 * One lane's answer (UWM or one live engine, asked in its own request) folded into the first:
 * its UWM result replaces the placeholder when it priced UWM, its rows, ineligible products and
 * sources join the rate-sheet result (a failed lane becomes a note), and the lane's sources are no
 * longer pending — the others stay marked "pricing…" until their own answers land.
 */
export function mergeLaneQuote(prev: QuoteApiResponse, lane: QuoteApiResponse, sources: string[], names: string[]): QuoteApiResponse {
  const union = (a: string[], b: string[]) => [...a, ...b.filter((x) => !a.includes(x))];
  const pending = (prev.pending ?? []).filter((s) => !sources.includes(s));
  const uwm = lane.uwm.status === 'skipped' ? prev.uwm : lane.uwm;
  if (lane.sheets.status === 'ok') {
    if (prev.sheets.status !== 'ok') return { ...prev, uwm, sheets: lane.sheets, pending };
    const q = prev.sheets.quote;
    const s = lane.sheets.quote;
    const seen = new Set(q.rows.map((r) => r.optionId));
    const seenIneligible = new Set(q.ineligible.map((i) => JSON.stringify(i)));
    return {
      ...prev,
      uwm,
      pending,
      sheets: {
        status: 'ok',
        quote: {
          ...q,
          rows: [...q.rows, ...s.rows.filter((r) => !seen.has(r.optionId))],
          ineligible: [...q.ineligible, ...s.ineligible.filter((i) => !seenIneligible.has(JSON.stringify(i)))],
          sheets: [...q.sheets, ...s.sheets.filter((x) => !q.sheets.some((y) => y.lenderKey === x.lenderKey && y.family === x.family && y.versionKey === x.versionKey))],
          warnings: union(q.warnings, s.warnings),
          unsupported: union(q.unsupported, s.unsupported),
          assumptions: union(q.assumptions, s.assumptions),
        },
      },
    };
  }
  const why = lane.sheets.status === 'error' ? lane.sheets.message : lane.sheets.reason;
  if (prev.sheets.status !== 'ok' || /not picked|no rate-sheet lender picked/i.test(why)) return { ...prev, uwm, pending };
  return { ...prev, uwm, pending, sheets: { status: 'ok', quote: { ...prev.sheets.quote, warnings: union(prev.sheets.quote.warnings, [`${names.join(', ')}: ${why}`]) } } };
}

export interface LayoutResponse {
  productType: ProductType;
  uwm: { fields: UwmLayoutField[] } | null;
  uwmError: { error: string; message: string } | null;
}

export type ScenarioSource = 'mana' | 'pricing' | 'other_lenders';

export interface ScenarioDto {
  borrowerName: string;
  email: string | null;
  phone: string | null;
  productType: ProductType;
  inputs: Record<string, unknown>;
  uwmInputs: Record<string, unknown> | null;
  lenders: ManaPricingSource[];
  cypBps: number;
  uwmCategory: string | null;
  rates: LenderRateRow[];
  selection: string[];
  /** The best-rates template the MLO chose (null = derived from the scenario). */
  emailTemplate?: ManaEmailTemplate | null;
  /** Rates pinned for the Realtor Marketing table. */
  realtorPicks?: RealtorPicks | null;
  ratesUpdatedAt: string | null;
  lastQuotedAt: string | null;
  /** When the scenario was last saved (Mana Pricing scenarios). */
  updatedAt?: string | null;
  source: ScenarioSource;
  notes: string[];
}

export interface ScheduleDto {
  id: string;
  borrowerName: string;
  /** grid / options: the borrower's address; realtor: extra addresses (the list comes from `realtorStates`). */
  toEmail: string;
  /** realtor: the states whose contacts get every send. */
  realtorStates?: string[];
  format: 'grid' | 'options' | 'realtor';
  productType: string;
  lenders: ManaPricingSource[];
  programKeys: string[];
  productIds: string[];
  cypBps: number;
  intervalMinutes: number;
  active: boolean;
  nextRunAt: string;
  lastRunAt: string | null;
  lastStatus: string | null;
  sentCount: number;
  legacySource: 'pricing' | 'other_lenders' | null;
  /** The connection every send goes out through (Integrations): the owner's Gmail or their SendGrid key. */
  transport?: 'gmail' | 'sendgrid';
  /** When the borrower's saved scenario (what every send uses) was last saved; null = the schedule sends its own frozen copy. */
  scenarioSavedAt: string | null;
  createdAt: string;
}

/** GET /api/mana-pricing/schedules/:id/scenario — the scenario a schedule sends, for the panel. */
export interface ScheduleScenarioDto {
  scenario: ScenarioDto;
  toEmail: string;
  /** True when no saved scenario exists and the schedule's frozen copy is shown. */
  fromSchedule: boolean;
  schedule: ScheduleDto;
}

export interface LegacyScheduleDto {
  source: 'pricing' | 'other_lenders';
  id: string;
  borrowerName: string;
  toEmail: string;
  format: 'grid' | 'options';
  productType: string;
  programKeys: string[];
  productIds: string[];
  cypBps: number;
  intervalMinutes: number;
  active: boolean;
  nextRunAt: string;
  lastRunAt: string | null;
  lastStatus: string | null;
  sentCount: number;
  createdAt: string;
}

export interface SchedulesResponse {
  schedules: ScheduleDto[];
  legacy: LegacyScheduleDto[];
}

export interface PreviewDto {
  subject: string;
  html: string;
  cells: ManaGridCell[];
  rows: ManaOptionsSentRow[];
  notes: string[];
}

export const SCHEDULES_KEY = ['mana-schedules'];

/** GET /api/mana-pricing/company-lenders — which lenders everyone's pricing includes. */
export interface CompanyLendersDto {
  lenders: Array<{ key: ManaPricingSource; name: string; included: boolean }>;
  canEdit: boolean;
}
export const COMPANY_LENDERS_KEY = ['mana-company-lenders'];

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export const fmtMoney0 = (v: number) => v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
export const fmtPct3 = (v: number) => `${v.toFixed(3)}%`;
export const fmtPrice = (v: number) => v.toFixed(3);

/** Central time, always ("Sep 23, 1:07 PM CT"). */
export const fmtWhen = (iso: string | null | undefined): string => fmtCT(iso);

/** One lender from GET /api/…/sheets — the fields the page header needs. */
export interface LenderSheetHeaderStatus {
  key: string;
  name: string;
  source: 'portal' | 'upload';
  current: Array<{ family: string; versionKey: string; sheetDateTime: string | null; sheetAt?: string | null; fetchedAt: string; hasPrices: boolean | null }>;
  /** Newest finished hourly pull/check. */
  lastCheck?: { at: string; status: string; downloaded: number; message: string | null } | null;
  /** How often the worker looks for a newer sheet (minutes); null = upload only. */
  checkEveryMinutes?: number | null;
  /** Set while the hourly pull is paused because the lender refused the saved login. */
  pullPausedAt?: string | null;
  /** Turned off for the company (Mana Pricing → Lenders): not priced anywhere. */
  pricingOff?: boolean;
  /** Priced live on this engine at quote time (the sheet supplies only the fees); null/absent = priced from the sheet. */
  liveEngine?: string | null;
}

/**
 * Header line per lender: the sheet that prices (its own publish time, in Central time), how
 * often the worker looks for a newer one, and what the last check saw.
 */
export function lenderSheetLine(l: LenderSheetHeaderStatus): { key: string; name: string; text: string; tone: 'ok' | 'warn' | 'muted'; title?: string } {
  const base = { key: l.key, name: l.name };
  const cur = l.current.find((c) => c.family === 'qm' || c.family === 'all') ?? null;
  const sheet = cur ? `sheet ${fmtSheetStamp(cur)}` : l.source === 'upload' ? 'no sheet uploaded yet' : 'no sheet pulled yet';
  const every = l.checkEveryMinutes == null ? null : l.checkEveryMinutes >= 60 ? 'every hour' : `every ${l.checkEveryMinutes} min`;
  const check = l.lastCheck ?? null;
  /** "checked every hour · last 1:07 PM CT" (a failed last check says so in its own line). */
  const cadence = every ? `checked ${every}${check?.status === 'ok' ? ` · last ${fmtWhen(check.at)}` : ''}` : null;
  if (l.pricingOff) return { ...base, text: `turned off for the company · ${l.liveEngine ? `live from ${l.liveEngine}` : sheet}`, tone: 'muted' };
  if (check?.status === 'error' && !l.pullPausedAt) {
    const failed = `last check failed ${fmtWhen(check.at)}${every ? ` (checks ${every})` : ''}`;
    const title = check.message?.replace(/\s*\|\s*screenshot .*$/, '') ?? undefined;
    return l.liveEngine
      ? { ...base, text: `live quotes from ${l.liveEngine} · fees from ${cur ? sheet : 'the rate sheet (none pulled yet)'} · ${failed}`, tone: 'warn', title }
      : { ...base, text: `${sheet} · ${failed}`, tone: 'warn', title };
  }
  // Live: every quote asks the lender's own engine; the sheet only supplies the lender fees.
  if (l.liveEngine) return { ...base, text: [`live quotes from ${l.liveEngine}`, `fees from ${cur ? sheet : 'the rate sheet (none pulled yet)'}`, cadence].filter(Boolean).join(' · '), tone: 'ok' };
  if (l.pullPausedAt) return { ...base, text: `${sheet} · hourly pull paused: the lender refused the saved login (re-save it in Integrations)`, tone: 'warn' };
  if (cur?.hasPrices === false) return { ...base, text: `${sheet} · template, no rates`, tone: 'warn' };
  if (l.source === 'upload') return { ...base, text: cur ? `${sheet} · uploaded ${fmtWhen(cur.fetchedAt)}` : sheet, tone: cur ? 'ok' : 'muted' };
  if (!cur) return { ...base, text: sheet, tone: 'muted' };
  if (check && check.downloaded === 0 && Date.parse(check.at) > Date.parse(cur.fetchedAt)) {
    return { ...base, text: [sheet, cadence, 'no newer sheet yet'].filter(Boolean).join(' · '), tone: 'ok' };
  }
  return { ...base, text: [sheet, `pulled ${fmtWhen(cur.fetchedAt)}`, cadence ?? 'checks hourly'].filter(Boolean).join(' · '), tone: 'ok' };
}

export function fmtInterval(min: number): string {
  if (min > 0 && min % 1440 === 0) return `${min / 1440} day${min === 1440 ? '' : 's'}`;
  if (min > 0 && min % 60 === 0) return `${min / 60} hour${min === 60 ? '' : 's'}`;
  return `${min} min`;
}

/** ISO instant → <input type="datetime-local"> value in LOCAL time. */
export function isoToLocalInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function localInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export function errText(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return (e.body as { message?: string })?.message ?? e.message;
  return e instanceof Error ? e.message : fallback;
}

export const numOf = (v: unknown): number => {
  const n = typeof v === 'string' ? Number(v.replace(/[^0-9.]/g, '')) : typeof v === 'number' ? v : NaN;
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const productTypeOf = (values: CanonicalValues): ProductType => (loanTypesOf(values).some((t) => /^heloc$/i.test(t)) ? 'heloc' : 'mortgage');

// ---------------------------------------------------------------------------
// Form rules
// ---------------------------------------------------------------------------

/**
 * One edit of the shared (rate-sheet catalog) fields: LTV ⇄ loan amount linked through the
 * property value, and option lists that depend on Loan Type reset when stale.
 */
export function applyCanonicalChange(prev: CanonicalValues, id: string, v: PricingFieldValue, driver: { current: 'loan' | 'ltv' }): CanonicalValues {
  if (id === 'baseLoanAmount') driver.current = 'loan';
  else if (id === 'ltv') driver.current = 'ltv';
  const next = { ...prev, [id]: v };
  const value = numOf(next.propertyValue);
  if (id === 'ltv') {
    const ltv = numOf(v);
    if (ltv > 0 && value > 0) next.baseLoanAmount = String(Math.round((value * ltv) / 100));
  } else if (id === 'baseLoanAmount') {
    const la = numOf(v);
    if (la > 0 && value > 0) next.ltv = ((la / value) * 100).toFixed(3);
  } else if (id === 'propertyValue' && value > 0) {
    const la = numOf(next.baseLoanAmount);
    const ltv = numOf(next.ltv);
    if (driver.current === 'ltv' && ltv > 0) next.baseLoanAmount = String(Math.round((value * ltv) / 100));
    else if (la > 0) next.ltv = ((la / value) * 100).toFixed(3);
    else if (ltv > 0) next.baseLoanAmount = String(Math.round((value * ltv) / 100));
  }
  // Second lien: the CLTV follows the loan, the senior balance and the value (read-only).
  if (['ltv', 'baseLoanAmount', 'propertyValue', 'seniorLienBalance', 'lienPosition', 'cltv'].includes(id)) {
    const la = numOf(next.baseLoanAmount);
    const senior = /second/i.test(String(next.lienPosition ?? '')) ? numOf(next.seniorLienBalance) : 0;
    next.cltv = la > 0 && value > 0 ? (((la + senior) / value) * 100).toFixed(3) : '';
  }
  if (id === 'loanType') {
    // Loan Type is a multi-select, but HELOC is its own product (home equity, UWM's HELOC form):
    // picking it clears the first-lien types, and picking a first-lien type clears it.
    const before = fieldValueList(prev.loanType);
    let picks = fieldValueList(v);
    const added = picks.filter((t) => !before.includes(t));
    if (added.includes('HELOC')) picks = ['HELOC'];
    else if (added.length && picks.includes('HELOC')) picks = picks.filter((t) => t !== 'HELOC');
    next.loanType = picks;
    for (const f of PRICING_FIELDS) {
      if (f.optionsBy?.field !== 'loanType') continue;
      const opts = fieldOptionsFor(f, next);
      if (!opts.includes(String(next[f.id] ?? ''))) next[f.id] = opts[0] ?? '';
    }
    // DSCR loans are investment properties only (every lender's DSCR program).
    if (picks.includes('DSCR') && !before.includes('DSCR')) next.occupancy = 'Investment Property';
  }
  // DSCR follows the rent and PITIA when both are entered (the ratio can still be typed directly).
  if ((id === 'monthlyRent' || id === 'monthlyPitia') && hasDscrLoanType(next)) {
    const ratio = dscrFromRent(next.monthlyRent, next.monthlyPitia);
    if (ratio) next.dscrRatio = ratio;
  }
  return next;
}

/** One edit of a UWM form field (HELOC's full form): the Pricing module's sales-price mirror and LTV linking. */
export function applyUwmChange(prev: UwmValues, itemId: string, v: UwmFieldValue, layout: UwmLayoutField[], driver: { current: 'loan' | 'ltv' }): UwmValues {
  if (itemId === 'LoanAmount') driver.current = 'loan';
  else if (itemId === 'LoanToValue') driver.current = 'ltv';
  const next = { ...prev, [itemId]: v };
  if (itemId === 'PurposeTypeId' && !['3', '12'].includes(typeof v === 'string' ? v : '')) {
    next['RefinancePurposeID'] = layout.find((x) => x.itemId === 'RefinancePurposeID')?.defaultValue ?? '';
  }
  if (itemId === 'SalesPrice' && prev['AppraisedValue'] === prev['SalesPrice']) next['AppraisedValue'] = v;
  else if (itemId === 'AppraisedValue' && prev['SalesPrice'] === prev['AppraisedValue']) next['SalesPrice'] = v;
  const value = numOf(next['AppraisedValue']) || numOf(next['SalesPrice']);
  if (itemId === 'LoanToValue') {
    const ltv = numOf(v);
    if (ltv > 0 && value > 0) next['LoanAmount'] = String(Math.round((value * ltv) / 100));
  } else if (itemId === 'LoanAmount') {
    const la = numOf(v);
    if (la > 0 && value > 0) next['LoanToValue'] = ((la / value) * 100).toFixed(3);
  } else if ((itemId === 'SalesPrice' || itemId === 'AppraisedValue') && value > 0) {
    const la = numOf(next['LoanAmount']);
    const ltv = numOf(next['LoanToValue']);
    if (driver.current === 'ltv' && ltv > 0) next['LoanAmount'] = String(Math.round((value * ltv) / 100));
    else if (la > 0) next['LoanToValue'] = ((la / value) * 100).toFixed(3);
    else if (ltv > 0) next['LoanAmount'] = String(Math.round((value * ltv) / 100));
  }
  return next;
}

const optionLabel = (layout: UwmLayoutField[] | null, itemId: string, id: string): string =>
  layout?.find((f) => f.itemId === itemId)?.selectListData?.find((o) => o.id === id)?.displayValue ?? '';

/** UWM's ★ category from the UWM form (the Pricing module's rule: purpose id + the first loan type). */
export function uwmCategoryOf(productType: ProductType, layout: UwmLayoutField[] | null, values: UwmValues): PricingCompCategory | null {
  const purposeTypeId = typeof values['PurposeTypeId'] === 'string' ? values['PurposeTypeId'] : null;
  const lt = values['LoanTypeIds'];
  const ltId = Array.isArray(lt) ? (lt[0] ?? '') : typeof lt === 'string' ? lt : '';
  return resolvePricingCategory({ productType, purposeTypeId, loanTypeDisplay: optionLabel(layout, 'LoanTypeIds', ltId) });
}

// US state from a ZIP's leading 3 digits (the email's LOAN PARAMETERS, as the Pricing module).
const ZIP3_STATES: Array<[number, number, string]> = [
  [6, 9, 'PR'], [10, 27, 'MA'], [28, 29, 'RI'], [30, 38, 'NH'], [39, 49, 'ME'], [50, 59, 'VT'],
  [60, 69, 'CT'], [70, 89, 'NJ'], [100, 149, 'NY'], [150, 196, 'PA'], [197, 199, 'DE'], [200, 205, 'DC'],
  [206, 219, 'MD'], [220, 246, 'VA'], [247, 268, 'WV'], [270, 289, 'NC'], [290, 299, 'SC'], [300, 319, 'GA'],
  [320, 349, 'FL'], [350, 369, 'AL'], [370, 385, 'TN'], [386, 397, 'MS'], [398, 399, 'GA'], [400, 427, 'KY'],
  [430, 459, 'OH'], [460, 479, 'IN'], [480, 499, 'MI'], [500, 528, 'IA'], [530, 549, 'WI'], [550, 567, 'MN'],
  [570, 577, 'SD'], [580, 588, 'ND'], [590, 599, 'MT'], [600, 629, 'IL'], [630, 658, 'MO'], [660, 679, 'KS'],
  [680, 693, 'NE'], [700, 714, 'LA'], [716, 729, 'AR'], [730, 749, 'OK'], [750, 799, 'TX'], [800, 816, 'CO'],
  [820, 831, 'WY'], [832, 838, 'ID'], [840, 847, 'UT'], [850, 865, 'AZ'], [870, 884, 'NM'], [889, 898, 'NV'],
  [900, 961, 'CA'], [967, 968, 'HI'], [970, 979, 'OR'], [980, 994, 'WA'], [995, 999, 'AK'],
];
export function zipToState(zip: string): string {
  const z = parseInt(String(zip).slice(0, 3), 10);
  if (!Number.isFinite(z)) return '';
  return ZIP3_STATES.find(([lo, hi]) => z >= lo && z <= hi)?.[2] ?? '';
}

/**
 * LOAN PARAMETERS of the grid email — from the UWM form exactly as the Pricing module builds
 * them; from the shared fields when UWM isn't available.
 */
export function gridParamsFor(layout: UwmLayoutField[] | null, values: UwmValues, canonical: CanonicalValues): ManaGridParams {
  const money = (n: number) => (n > 0 ? `$${Math.round(n).toLocaleString('en-US')}` : '');
  // The shared Waive Escrows field drives every lender (UWM's escrow waiver follows it).
  const waiveEscrows = /^yes$/i.test(String(canonical.waiveEscrows ?? '')) ? 'Yes' : 'No';
  if (layout && Object.keys(values).length) {
    const display = (itemId: string) => {
      const v = values[itemId];
      return typeof v === 'string' ? optionLabel(layout, itemId, v) || v : '';
    };
    const purposeDisp = display('PurposeTypeId');
    const refiDisp = display('RefinancePurposeID');
    let loanPurpose = purposeDisp;
    if (/refinance/i.test(purposeDisp)) loanPurpose = /cash[\s-]?out/i.test(refiDisp) ? 'Cash-Out' : 'Rate/Term Refinance';
    else if (/purchase/i.test(purposeDisp)) loanPurpose = 'Purchase';
    const num = (itemId: string) => {
      const v = values[itemId];
      const n = typeof v === 'string' ? Number(v) : NaN;
      return Number.isFinite(n) ? n : 0;
    };
    return {
      loanAmount: money(num('LoanAmount') || num('TotalLineAmount')),
      appraisedValue: money(num('AppraisedValue')),
      creditScore: typeof values['CreditScore'] === 'string' ? values['CreditScore'] : '',
      state: zipToState(typeof values['PropertyZipCode'] === 'string' ? values['PropertyZipCode'] : ''),
      occupancy: display('OccupancyTypeId'),
      loanPurpose,
      loanType: emailLoanTypeLabel(canonical),
      waiveEscrows,
    };
  }
  return {
    loanAmount: money(numOf(canonical.baseLoanAmount)),
    appraisedValue: money(numOf(canonical.propertyValue)),
    creditScore: String(canonical.creditScore ?? ''),
    state: String(canonical.state ?? '').trim().toUpperCase().slice(0, 2) || zipToState(String(canonical.propertyZip ?? '')),
    occupancy: String(canonical.occupancy ?? ''),
    loanPurpose: emailLoanPurposeLabel(canonical.loanPurpose) || 'Purchase',
    loanType: emailLoanTypeLabel(canonical),
    waiveEscrows,
  };
}

export const trayKey = (r: LenderRateRow) => `${r.productId ?? 'manual'}|${r.product}|${r.rate}|${r.points}`;

/** The shared field that sets each UWM request key the panel manages (for UWM's field errors). */
const UWM_KEY_TO_FIELD: Record<string, string> = {
  loanTypeIds: 'loanType',
  loanAmount: 'baseLoanAmount',
  appraisedValue: 'propertyValue',
  salesPrice: 'propertyValue',
  loanToValue: 'ltv',
  purposeTypeId: 'loanPurpose',
  refinancePurposeID: 'loanPurpose',
  occupancyTypeId: 'occupancy',
  propertyTypeID: 'legalStructure',
  numberOfUnits: 'numberOfUnits',
  attachmentTypeID: 'attachmentType',
  propertyZipCode: 'propertyZip',
  creditScore: 'creditScore',
  monthlyIncome: 'monthlyQualifyingIncome',
  isDTIOver45Percent: 'dtiRatio',
  firstTimeHomeBuyer: 'firstTimeHomeBuyer',
  loanTermIds: 'term',
  commitmentPeriodID: 'desiredLockPeriod',
  escrowWaiverTypeId: 'waiveEscrows',
  compensationPayerTypeID: 'compType',
  // UWM's HELOC form
  firstLienAmount: 'seniorLienBalance',
  totalLineAmount: 'baseLoanAmount',
  initialDrawAmount: 'initialDraw',
  homeEquityTypeId: 'homeEquityProduct',
};

/** A readable name for a UWM field error key: the shared field's label when the panel sets it, else UWM's own label. */
export function uwmErrorLabel(key: string, layout: UwmLayoutField[] | null, productType: ProductType): string {
  void productType;
  const lower = key.charAt(0).toLowerCase() + key.slice(1);
  const shared = PRICING_FIELDS.find((f) => f.id === UWM_KEY_TO_FIELD[lower]);
  if (shared) return shared.label;
  return layout?.find((f) => f.itemId.toLowerCase() === key.toLowerCase())?.label ?? key;
}
